import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Edit3,
  Trash2,
  CheckCircle,
  Image as ImageIcon,
  Sparkles,
  Layers,
  CheckSquare,
  Save,
  X,
  Plus,
  Upload,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import {
  QuestionItem,
  ModuleItem,
} from "../data/interfaces/QuestionBankInterfaces";
import CustomSelect from "../components/CustomSelect";
import RightAnswerSelect from "../components/RightAnswerSelect";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import ImagePreviewModal from "../../../../app/shared/components/ImagePreviewModal";
import "../styles/QuestionBankStyles.css";

const QuestionDetailPage: React.FC = () => {
  const location = useLocation();
  const params = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const questionId = location.state?.questionId || params.questionId || "";

  const [question, setQuestion] = useState<QuestionItem | null>(null);
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  // Toggle for Edit mode on the same page
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Edit Form Fields
  const [questionText, setQuestionText] = useState<string>("");
  const [difficultyLevel, setDifficultyLevel] = useState<string>("Easy");
  const [moduleId, setModuleId] = useState<string>("");
  const [moduleName, setModuleName] = useState<string>("");
  const [optA, setOptA] = useState<string>("");
  const [optB, setOptB] = useState<string>("");
  const [optC, setOptC] = useState<string>("");
  const [optD, setOptD] = useState<string>("");
  const [correctOption, setCorrectOption] = useState<string>("A");
  const [explanation, setExplanation] = useState<string>("");
  const [concepts, setConcepts] = useState<string>("");

  // Image Upload File & Existing URL
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [previewModalSrc, setPreviewModalSrc] = useState<string | null>(null);

  // Add Module Modal state
  const [showAddModuleModal, setShowAddModuleModal] = useState<boolean>(false);
  const [newModuleName, setNewModuleName] = useState<string>("");
  const [savingModule, setSavingModule] = useState<boolean>(false);

  // Delete Alert State
  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchModulesAndQuestion = async () => {
    if (!questionId) return;
    setLoading(true);
    try {
      // 1. Fetch modules
      const modRes = await QuestionBankClient.listModules();
      let modItems: ModuleItem[] = [];
      if (modRes && modRes.modules) {
        modItems = modRes.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
      }

      // 2. Fetch question details
      const res = await QuestionBankClient.getQuestion(questionId);
      const qData = res.question || res;
      if (qData) {
        setQuestion(qData);
        populateEditForm(qData, modItems);
      }
    } catch (err) {
      console.error("Error fetching question details:", err);
      setAlertConfig({
        show: true,
        title: "Fetch Error",
        message: "Question details could not be loaded.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const populateEditForm = (qData: QuestionItem, modItems: ModuleItem[]) => {
    setQuestionText(qData.question || "");
    setDifficultyLevel(qData.difficulty_level || "Easy");
    setExplanation(qData.explanation || "");
    setConcepts(
      Array.isArray(qData.concepts)
        ? qData.concepts.join(", ")
        : qData.concepts || "",
    );
    setExistingImageUrl(qData.image_url || null);
    setImageFile(null);
    setImagePreviewUrl(null);

    if (qData.module_id) {
      setModuleId(qData.module_id);
      const found = modItems.find((m) => m.id === qData.module_id);
      setModuleName(found ? found.name : qData.module_name || "");
    } else if (modItems.length > 0) {
      setModuleId(modItems[0].id);
      setModuleName(modItems[0].name);
    }

    if (qData.answers && qData.answers.length > 0) {
      setOptA(qData.answers[0]?.answer || "");
      if (qData.answers[0]?.correct_answer) setCorrectOption("A");

      if (qData.answers[1]) {
        setOptB(qData.answers[1].answer || "");
        if (qData.answers[1].correct_answer) setCorrectOption("B");
      }
      if (qData.answers[2]) {
        setOptC(qData.answers[2].answer || "");
        if (qData.answers[2].correct_answer) setCorrectOption("C");
      }
      if (qData.answers[3]) {
        setOptD(qData.answers[3].answer || "");
        if (qData.answers[3].correct_answer) setCorrectOption("D");
      }
    }
  };

  useEffect(() => {
    fetchModulesAndQuestion();
  }, [questionId]);

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newModuleName.trim()) return;
    setSavingModule(true);
    try {
      const res = await QuestionBankClient.createModule(newModuleName.trim());
      setShowAddModuleModal(false);
      setNewModuleName("");
      const modRes = await QuestionBankClient.listModules();
      if (modRes && modRes.modules) {
        const modItems = modRes.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
      }
      if (res && res.module) {
        setModuleId(res.module.module_id);
        setModuleName(res.module.module_name);
      }
      setAlertConfig({
        show: true,
        title: "Module Created",
        message: `Module '${newModuleName}' created and selected successfully.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Error creating module:", err);
    } finally {
      setSavingModule(false);
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "You can only upload 1 image per question. Please select a single image file.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Invalid file type. Only image files (PNG, JPG, JPEG, WEBP, SVG) are allowed.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Image file size exceeds the allowed limit of 5MB.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;
      if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
        setAlertConfig({
          show: true,
          title: "Error",
          message: `Image dimensions exceed the allowed limit. Maximum allowed is ${MAX_WIDTH}x${MAX_HEIGHT}, but got ${img.width}x${img.height}.`,
          type: "error",
        });
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
        return;
      }
      setImageFile(file);
      setImagePreviewUrl(objectUrl);
    };
    img.onerror = () => {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Failed to load the selected image file. Please choose a valid image file.",
        type: "error",
      });
      URL.revokeObjectURL(objectUrl);
      e.target.value = "";
    };
    img.src = objectUrl;
  };

  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionId) return;
    if (!questionText.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Question description is required.",
        type: "warning",
      });
      return;
    }
    if (!optA.trim() || !optB.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Options A and B are required.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const answersPayload = [
        { answer: optA, correct_answer: correctOption === "A" },
        { answer: optB, correct_answer: correctOption === "B" },
      ];
      if (optC.trim())
        answersPayload.push({
          answer: optC,
          correct_answer: correctOption === "C",
        });
      if (optD.trim())
        answersPayload.push({
          answer: optD,
          correct_answer: correctOption === "D",
        });

      let finalImageUrl = existingImageUrl || undefined;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        formData.append("question_id", questionId);
        formData.append("module_id", moduleId);
        const imgRes = await QuestionBankClient.uploadQuestionImage(formData);
        finalImageUrl = imgRes.image_url;
      }

      const updatePayload = {
        question: questionText.trim(),
        difficulty_level: difficultyLevel as any,
        module_id: moduleId,
        module_name: moduleName,
        explanation: explanation || undefined,
        concepts: concepts
          ? concepts
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : undefined,
        answers: answersPayload as any,
        image_url: finalImageUrl,
      };

      await QuestionBankClient.updateQuestion(questionId, updatePayload);
      setAlertConfig({
        show: true,
        title: "Success",
        message: "Question updated successfully!",
        type: "success",
      });

      // Re-fetch updated question from backend and exit edit mode
      await fetchModulesAndQuestion();
      setIsEditing(false);
    } catch (err: any) {
      console.error("Error updating question:", err);
      setAlertConfig({
        show: true,
        title: "Update Failed",
        message: err?.response?.data?.detail || "Failed to update question.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!questionId) return;
    setShowDeleteAlert(false);
    setDeleting(true);
    try {
      await QuestionBankClient.deleteQuestion(questionId, question?.module_id);
      setAlertConfig({
        show: true,
        title: "Deleted",
        message: "Question has been deleted from Question Bank.",
        type: "success",
      });
      setTimeout(() => {
        navigate("/dashboard/question-bank");
      }, 1000);
    } catch (err) {
      console.error("Error deleting question:", err);
      setDeleting(false);
    }
  };

  if (loading || deleting) {
    return (
      <Loader
        isLoading={true}
        message={deleting ? "Deleting Question..." : "Loading Question Details..."}
      />
    );
  }

  if (!question) {
    return (
      <div className="qb-container">
        <Alert
          show={true}
          type="error"
          title="Not Found"
          message="Question details not found."
          onClose={() => navigate("/dashboard/question-bank")}
        />
        <button
          className="qb-btn qb-btn-outline"
          style={{ marginTop: "16px" }}
          onClick={() => navigate("/dashboard/question-bank")}
        >
          <ArrowLeft size={16} /> Back to Question Bank
        </button>
      </div>
    );
  }

  return (
    <div className="qb-container">
      <Loader
        isLoading={saving || savingModule}
        message={savingModule ? "Saving Module to DB..." : "Saving Changes..."}
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Header */}
      <div className="qb-header">
        <div>
          {isEditing ? (
            <h1 className="qb-title">
              <Edit3 size={24} /> Edit Question
            </h1>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <span
                className={`qb-badge qb-badge-${(question.difficulty_level || "Easy").toLowerCase()}`}
              >
                {question.difficulty_level || "Easy"}
              </span>
              <div className="qb-module-badge">
                <Layers size={15} color="#0d6e5b" />
                <span className="qb-module-badge-label">Module:</span>
                <strong className="qb-module-badge-value">
                  {question.module_name || "General"}
                </strong>
              </div>
            </div>
          )}
        </div>

        {!isEditing && (
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <button
              className="qb-btn qb-btn-outline"
              onClick={() => navigate("/dashboard/question-bank")}
            >
              <ArrowLeft size={16} /> Back to List
            </button>
            <button
              className="qb-btn qb-btn-primary"
              onClick={() => {
                populateEditForm(question, modules);
                setIsEditing(true);
              }}
            >
              <Edit3 size={16} /> Edit Question
            </button>
            <button
              className="qb-btn qb-btn-danger"
              onClick={() => setShowDeleteAlert(true)}
            >
              <Trash2 size={16} /> Delete
            </button>
          </div>
        )}
      </div>

      {/* MAIN CONTENT AREA */}
      {!isEditing ? (
        /* READ-ONLY VIEW MODE */
        <div className="qb-content-scroll">
          {/* Section 1: Question Description */}
          <div className="qb-form-section">
            <div className="qb-section-header">
              <Layers size={20} color="#0d6e5b" />
              <span>Question Description</span>
            </div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "400",
                color: "#07473b",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              {question.question}
            </h3>
          </div>

          {/* Section 2: Diagram / Image Asset */}
          {question.image_url && (
            <div className="qb-form-section">
              <div className="qb-section-header">
                <ImageIcon size={20} color="#0d6e5b" />
                <span>Diagram / Image Asset</span>
              </div>
              <div
                style={{
                  textAlign: "center",
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "10px",
                  border: "1px solid #dde5e2",
                }}
              >
                <div
                  style={{ cursor: "pointer", display: "inline-block" }}
                  onClick={() => setPreviewModalSrc(question.image_url || null)}
                  title="Click to view full image preview"
                >
                  <img
                    src={question.image_url}
                    alt="Question Diagram Asset"
                    className="qb-clickable-preview-img"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "320px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      transition: "all 0.2s ease",
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Section 3: MCQ Options */}
          <div className="qb-form-section">
            <div className="qb-section-header">
              <CheckSquare size={20} color="#0d6e5b" />
              <span>Response Options</span>
            </div>

            {question.answers && question.answers.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {question.answers.map((opt, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "14px 18px",
                      borderRadius: "10px",
                      border: opt.correct_answer
                        ? "2px solid #0d6e5b"
                        : "1px solid #dde5e2",
                      backgroundColor: opt.correct_answer
                        ? "#e6f4f1"
                        : "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "15px",
                        color: opt.correct_answer ? "#07473b" : "#334155",
                        fontWeight: opt.correct_answer ? "600" : "400",
                      }}
                    >
                      <strong
                        style={{
                          width: "32px",
                          display: "inline-block",
                          color: "#07473b",
                        }}
                      >
                        {String.fromCharCode(65 + idx)}.
                      </strong>{" "}
                      {opt.answer}
                    </span>
                    {opt.correct_answer && (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "#0d6e5b",
                          fontSize: "13px",
                          fontWeight: "700",
                          padding: "4px 12px",
                          background: "#ffffff",
                          borderRadius: "16px",
                          border: "1px solid #0d6e5b",
                        }}
                      >
                        <CheckCircle size={16} /> Correct Key
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: "13px", color: "#64748b" }}>
                No options defined.
              </p>
            )}
          </div>

          {/* Section 4: Explanation */}
          {question.explanation && (
            <div className="qb-form-section">
              <div className="qb-section-header">
                <Sparkles size={20} color="#0d6e5b" />
                <span>Solution Explanation</span>
              </div>
              <div
                style={{
                  padding: "16px",
                  background: "#f0fdf4",
                  borderRadius: "10px",
                  border: "1px solid #bbf7d0",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    color: "#15803d",
                    margin: 0,
                    lineHeight: "1.6",
                  }}
                >
                  {question.explanation}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* INTERACTIVE EDIT MODE */
        <form onSubmit={handleSaveChanges} className="qb-single-frame-form">
          <div className="qb-form-body-scroll">
            {/* Section 1: Compact Module & Difficulty Bar */}
            <div className="qb-compact-module-bar">
              <div className="qb-compact-bar-item">
                <span className="qb-compact-bar-label">Difficulty Level:</span>
                <div className="qb-difficulty-pills" style={{ gap: "6px" }}>
                  {["Easy", "Medium", "Hard"].map((diff) => (
                    <div
                      key={diff}
                      className={`qb-diff-pill ${difficultyLevel === diff ? `active-${diff.toLowerCase()}` : ""}`}
                      onClick={() => setDifficultyLevel(diff)}
                      style={{ padding: "6px 16px", fontSize: "13px" }}
                    >
                      {diff}
                    </div>
                  ))}
                </div>
              </div>

              <div className="qb-compact-bar-item" style={{ gap: "10px" }}>
                <span className="qb-compact-bar-label">
                  Target Module <span style={{ color: "#ef4444" }}>*</span>:
                </span>
                {modules.length > 0 ? (
                  <>
                    <div style={{ width: "220px" }}>
                      <CustomSelect
                        options={modules.map((m) => ({
                          value: m.id,
                          label: m.name,
                        }))}
                        value={moduleId}
                        onChange={(val) => {
                          setModuleId(val);
                          const selectedMod = modules.find((m) => m.id === val);
                          if (selectedMod) setModuleName(selectedMod.name);
                        }}
                        placeholder="Select Module..."
                      />
                    </div>
                    <button
                      type="button"
                      className="qb-btn qb-btn-outline"
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => setShowAddModuleModal(true)}
                    >
                      <Plus size={14} /> Add New Module
                    </button>
                  </>
                ) : (
                  <div
                    style={{
                      padding: "6px 14px",
                      backgroundColor: "#FEF3C7",
                      borderRadius: "8px",
                      color: "#92400E",
                      fontSize: "13px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "16px",
                      minWidth: "260px",
                    }}
                  >
                    <span>No modules found</span>
                    <button
                      type="button"
                      className="qb-btn qb-btn-primary"
                      style={{ padding: "4px 12px", fontSize: "12px", whiteSpace: "nowrap" }}
                      onClick={() => setShowAddModuleModal(true)}
                    >
                      Create Module
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Side-by-Side Question Description & Image Upload (Image 3 Layout) */}
            <div className="qb-desc-image-grid">
              {/* Left Column: Question Description */}
              <div className="qb-desc-card">
                <label className="qb-desc-label">
                  Question Description{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  className="qb-desc-textarea"
                  placeholder="Type your question description..."
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  required
                />
              </div>

              {/* Right Column: Upload Image (Image 3 Layout Box) */}
              <div className="qb-image-upload-card">
                <label className="qb-image-upload-label">Upload Image</label>
                <div className="qb-image-dropzone-box">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ display: "none" }}
                    id="qb-detail-edit-image-upload-input"
                  />
                  {imagePreviewUrl || existingImageUrl ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                        }}
                        onClick={() =>
                          setPreviewModalSrc(imagePreviewUrl || existingImageUrl)
                        }
                        title="Click to view image preview"
                      >
                        <img
                          src={imagePreviewUrl || existingImageUrl || ""}
                          alt="Asset Preview"
                          className="qb-clickable-preview-img"
                          style={{
                            height: "50px",
                            maxWidth: "100px",
                            objectFit: "contain",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            transition: "all 0.2s ease",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          marginLeft: "auto",
                        }}
                      >
                        <label
                          htmlFor="qb-detail-edit-image-upload-input"
                          className="qb-image-upload-btn"
                          style={{ padding: "4px 10px", fontSize: "11px" }}
                        >
                          Replace
                        </label>
                        {imagePreviewUrl && (
                          <button
                            type="button"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreviewUrl(null);
                            }}
                            className="qb-btn qb-btn-danger"
                            style={{ padding: "2px 8px", fontSize: "11px" }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: "500",
                        }}
                      >
                        No image selected
                      </span>
                      <label
                        htmlFor="qb-detail-edit-image-upload-input"
                        className="qb-image-upload-btn"
                      >
                        <Upload size={14} /> Upload
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3 & 4: Response Options, Concepts & Explanation Container */}
            <div className="qb-response-section-compact">
              {/* Header: Title on Left, Right Answer Select on Top Right */}
              <div className="qb-response-header">
                <span className="qb-response-title">Response Options</span>
                <RightAnswerSelect
                  value={correctOption}
                  onChange={setCorrectOption}
                />
              </div>

              {/* 4-Column Options Grid */}
              <div className="qb-options-grid-4col">
                {[
                  { key: "A", val: optA, setVal: setOptA },
                  { key: "B", val: optB, setVal: setOptB },
                  { key: "C", val: optC, setVal: setOptC },
                  { key: "D", val: optD, setVal: setOptD },
                ].map((item) => (
                  <div
                    key={item.key}
                    className={`qb-compact-option-card ${correctOption === item.key ? "active-correct" : ""}`}
                    onClick={() => setCorrectOption(item.key)}
                  >
                    <div className="qb-compact-option-header">
                      <span className="qb-option-letter-badge">{item.key}</span>
                    </div>
                    <textarea
                      className="qb-option-textarea"
                      placeholder={`Option ${item.key} description...`}
                      value={item.val}
                      onChange={(e) => item.setVal(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      required
                    />
                  </div>
                ))}
              </div>

              {/* Bottom 2-Column Layout: Concepts & Explanation */}
              <div className="qb-bottom-2col-grid">
                <div className="qb-bottom-col-card">
                  <label className="qb-bottom-col-label">Concepts</label>
                  <textarea
                    className="qb-bottom-textarea"
                    placeholder="Concept-1, Concept-2"
                    value={concepts}
                    onChange={(e) => setConcepts(e.target.value)}
                  />
                </div>

                <div className="qb-bottom-col-card">
                  <label className="qb-bottom-col-label">
                    Explanation (Optional)
                  </label>
                  <textarea
                    className="qb-bottom-textarea"
                    placeholder="Explanation for the correct answer..."
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pinned Fixed Bottom Action Bar */}
          <div className="qb-fixed-action-bar">
            <button
              type="button"
              className="qb-btn qb-btn-outline"
              onClick={() => {
                populateEditForm(question, modules);
                setIsEditing(false);
              }}
              style={{ padding: "8px 20px" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="qb-btn qb-btn-primary"
              style={{ padding: "8px 24px" }}
            >
              <Save size={16} /> Save Changes
            </button>
          </div>
        </form>
      )}

      {/* Add New Module Modal */}
      {showAddModuleModal && ReactDOM.createPortal(
        <div
          className="qb-modal-overlay"
          onClick={() => setShowAddModuleModal(false)}
        >
          <div
            className="qb-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "480px" }}
          >
            <div className="qb-modal-header">
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#07473b",
                }}
              >
                Create New Module
              </h3>
              <button
                className="qb-modal-close"
                onClick={() => setShowAddModuleModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateModule}>
              <div className="qb-form-group">
                <label className="qb-label">
                  Module Name <span style={{ color: "#ef4444" }}>*</span>:
                </label>
                <input
                  type="text"
                  className="qb-input"
                  placeholder="e.g. System Design, Cloud Computing, Algorithms"
                  value={newModuleName}
                  onChange={(e) => setNewModuleName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="qb-btn qb-btn-outline"
                  onClick={() => setShowAddModuleModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="qb-btn qb-btn-primary">
                  Create Module
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Alert Modal */}
      {showDeleteAlert && ReactDOM.createPortal(
        <div
          className="qb-modal-overlay"
          onClick={() => setShowDeleteAlert(false)}
        >
          <div
            className="qb-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "440px" }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#b91c1c",
                marginBottom: "12px",
              }}
            >
              Delete Question?
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "20px",
              }}
            >
              Are you sure you want to delete this question? This action cannot
              be undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                className="qb-btn qb-btn-outline"
                onClick={() => setShowDeleteAlert(false)}
              >
                Cancel
              </button>
              <button className="qb-btn qb-btn-danger" onClick={handleDelete}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Image Preview Lightbox Modal */}
      <ImagePreviewModal
        src={previewModalSrc}
        onClose={() => setPreviewModalSrc(null)}
      />
    </div>
  );
};

export default QuestionDetailPage;
